import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fluencyApi } from "../api/fluencyApi";
import { flashcardKeys } from "@/features/flashcards/hooks/useFlashcards";
import type {
  FluencyPassageGenerateInput,
  FluencyOutcome,
  FluencySessionStartInput,
} from "../types/fluency";

// ==================== Query Keys ====================
export const fluencyKeys = {
  all: ["fluency"] as const,
  passages: (studentId: string) => [...fluencyKeys.all, "passages", studentId] as const,
  session: (sessionId: string) => [...fluencyKeys.all, "session", sessionId] as const,
  sessionsForStudent: (studentId: string) => [...fluencyKeys.all, "sessions", studentId] as const,
};

// ==================== Passage Hooks ====================

export const useGeneratePassage = () =>
  useMutation({
    mutationFn: (input: FluencyPassageGenerateInput) => fluencyApi.generatePassage(input),
  });

export const usePassages = (studentId: string, enabled = true) =>
  useQuery({
    queryKey: fluencyKeys.passages(studentId),
    queryFn: () => fluencyApi.listPassages(studentId),
    enabled: enabled && !!studentId,
  });

// ==================== Session Hooks ====================

export const useStartSession = () =>
  useMutation({
    mutationFn: (input: FluencySessionStartInput) => fluencyApi.startSession(input),
  });

export const useStartReading = () =>
  useMutation({
    mutationFn: (sessionId: string) => fluencyApi.startReading(sessionId),
  });

export const useEndSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, outcome }: { sessionId: string; outcome: FluencyOutcome }) =>
      fluencyApi.endSession(sessionId, outcome),
    onSuccess: (report, vars) => {
      // Refresh the session itself so the page rerenders with the final state.
      qc.invalidateQueries({ queryKey: fluencyKeys.session(vars.sessionId) });
      // Refresh the student's session history (Read Aloud tab on child profile).
      if (report.session_id) {
        qc.invalidateQueries({ queryKey: fluencyKeys.all });
      }
      // Feedback pipeline may have added flashcards or assignments — refresh those caches.
      qc.invalidateQueries({ queryKey: flashcardKeys.decks() });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["students"] }); // child decks queries
    },
  });
};

export const useSession = (sessionId: string | null) =>
  useQuery({
    queryKey: sessionId
      ? fluencyKeys.session(sessionId)
      : (["fluency", "session", "none"] as const),
    queryFn: () => fluencyApi.getSession(sessionId as string),
    enabled: !!sessionId,
  });

export const useStudentSessions = (studentId: string, enabled = true) =>
  useQuery({
    queryKey: fluencyKeys.sessionsForStudent(studentId),
    queryFn: () => fluencyApi.listSessions(studentId),
    enabled: enabled && !!studentId,
  });
